<script setup lang="ts">
import { computed } from 'vue';
import SavedViewIcon from '@/components/savedViews/SavedViewIcon.vue';
import { useSavedViewAppearances } from '@/composables/useSavedViewAppearances';
import { modelIcon } from '@/composables/modelIcons';
import { areaEntity, entityGate, recordPathFor, savedViewIsOpenable, viewPathFor } from '@/navigation/areas';
import { isGateOpen } from '@/navigation/gates';
import { groupRecentItemsByDay, useRecentItemsStore } from '@/stores/recentItemsStore';

/**
 * The records and saved views you opened lately, grouped by day: "Today",
 * "Yesterday" and so on. Home's sidebar shows it on a desktop, and Home itself
 * shows it on a phone, where the sidebar is out of reach.
 */
const recentItems = useRecentItemsStore();
const appearanceOf = useSavedViewAppearances();
/**
 * Only what the user can still open: a record type they lost access to, or
 * one no area holds any more, drops out instead of linking to "not found".
 */
const openable = computed(() =>
    recentItems.items.filter((item) =>
        item.kind === 'view'
            ? viewPathFor(item.entityId, item.id) !== null && savedViewIsOpenable(item.entityId, isGateOpen)
            : areaEntity(item.entityId) !== null && isGateOpen(entityGate(item.entityId)),
    ),
);
const groups = computed(() =>
    groupRecentItemsByDay(openable.value).map((group) => ({
        label: group.label,
        links: group.items.map((item) => ({
            key: `${item.kind}:${item.entityId}:${item.id}`,
            label: item.label,
            // A record shows its model's icon. A saved view shows its own icon and colour.
            icon: item.kind === 'view' ? null : modelIcon(item.entityId),
            view: item.kind === 'view' ? (appearanceOf(item.id) ?? {}) : null,
            to: item.kind === 'view' ? viewPathFor(item.entityId, item.id)! : recordPathFor(item.entityId, item.id),
        })),
    })),
);
</script>

<template>
  <section v-for="group in groups" :key="group.label" class="flex flex-col gap-0.5" data-testid="home-recent-group">
    <h3 class="flex h-7 items-center px-2 text-xs font-medium text-muted-foreground">{{ group.label }}</h3>
    <router-link
      v-for="link in group.links"
      :key="link.key"
      :to="link.to"
      data-testid="home-recent-link"
      class="flex h-8 min-w-0 items-center gap-2.5 rounded-lg px-2 text-sm text-muted-foreground hover:bg-sidebar-accent hover:text-foreground"
    >
      <SavedViewIcon v-if="link.view" :appearance="link.view" class="size-4" />
      <component :is="link.icon" v-else-if="link.icon" class="size-4 shrink-0" aria-hidden="true" />
      <span class="truncate">{{ link.label }}</span>
    </router-link>
  </section>
  <p v-if="groups.length === 0" class="px-2 text-xs text-muted-foreground" data-testid="home-recent-empty">Records you open show here.</p>
</template>
